import { useState, useMemo } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Copy } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/Calendar';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAvailability } from '@/hooks/useAvailability';
import { SHIFT_LABELS } from '@/types';
import type { ShiftType } from '@/types';
import toast from 'react-hot-toast';

export function AvailabilityPage() {
  const today = new Date();
  const from = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const to = format(addDays(new Date(from), 60), 'yyyy-MM-dd');

  const { slots, templates, loading, createSlots, removeSlot, applyTemplate } = useAvailability(from, to);
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [newShift, setNewShift] = useState<ShiftType>('morning');
  const [saving, setSaving] = useState(false);

  const markedDates = useMemo(() => {
    const marks: Record<string, { color: string; label?: string }> = {};
    for (const slot of slots) {
      const colors: Record<string, string> = {
        available: '#16a34a',
        tentative: '#eab308',
        booked: '#3b82f6',
        cancelled: '#9ca3af',
      };
      marks[slot.date] = { color: colors[slot.status] || '#16a34a' };
    }
    return marks;
  }, [slots]);

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const slotsForDate = slots.filter(s => s.date === selectedDateStr);

  const handleAddSlot = async () => {
    try {
      setSaving(true);
      await createSlots([{ date: selectedDateStr, shift: newShift, status: 'available' }]);
      setShowAddSlot(false);
      toast.success('Disponibilidad registrada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async () => {
    try {
      setSaving(true);
      const fromDate = format(today, 'yyyy-MM-dd');
      const toDate = format(addDays(today, 30), 'yyyy-MM-dd');
      await applyTemplate(fromDate, toDate);
      toast.success('Plantilla aplicada para los próximos 30 días');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl tracking-wider">DISPONIBILIDAD</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowTemplate(true)}>
            <Copy size={14} className="mr-1" /> Plantilla
          </Button>
          <Button size="sm" onClick={() => setShowAddSlot(true)}>
            <Plus size={14} className="mr-1" /> Agregar
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Disponible</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Tentativo</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Reservado</span>
      </div>

      {/* Calendar */}
      <Card padding="sm">
        <Calendar selectedDate={selectedDate} onDateSelect={setSelectedDate} markedDates={markedDates} />
      </Card>

      {/* Slots for selected date */}
      <div>
        <h2 className="font-heading text-lg tracking-wider mb-2">
          {format(selectedDate, "EEEE d 'de' MMMM", { locale: es }).toUpperCase()}
        </h2>
        {slotsForDate.length === 0 ? (
          <Card className="text-center py-6">
            <p className="text-sm text-gray-500">Sin disponibilidad declarada</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setShowAddSlot(true)}>
              Agregar turno
            </Button>
          </Card>
        ) : (
          <div className="space-y-2">
            {slotsForDate.map(slot => (
              <Card key={slot.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{SHIFT_LABELS[slot.shift as ShiftType] || slot.shift}</p>
                  {slot.zone_name && <p className="text-xs text-gray-500">{slot.zone_name}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={slot.status} />
                  {slot.status === 'available' && (
                    <button
                      onClick={() => removeSlot(slot.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add slot modal */}
      <Modal isOpen={showAddSlot} onClose={() => setShowAddSlot(false)} title="Agregar disponibilidad">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">{format(selectedDate, "EEEE d 'de' MMMM yyyy", { locale: es })}</p>
          <Select
            label="Turno"
            value={newShift}
            onChange={(e) => setNewShift(e.target.value as ShiftType)}
            options={Object.entries(SHIFT_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          <Button fullWidth onClick={handleAddSlot} loading={saving}>Agregar</Button>
        </div>
      </Modal>

      {/* Template modal */}
      <Modal isOpen={showTemplate} onClose={() => setShowTemplate(false)} title="Plantilla semanal">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {templates.length > 0
              ? `Tienes ${templates.length} turnos configurados en tu plantilla.`
              : 'No tienes una plantilla configurada. Configúrala en la app y aplícala para generar disponibilidad automáticamente.'
            }
          </p>
          <Button fullWidth onClick={handleApplyTemplate} loading={saving}>
            Aplicar plantilla (30 días)
          </Button>
        </div>
      </Modal>
    </div>
  );
}
